/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { message, Button, Upload, Select, Spin, Checkbox, Card, Modal } from "antd";
import {
  UploadOutlined,
  CloudDownloadOutlined,
} from "@ant-design/icons";
import axiosInstance from "../../../../api/Axios";
import * as XLSX from "xlsx";

const { Dragger } = Upload;

/* Simple icons used in original component */
const CheckMark = () => (
  <span style={{ color: "green", marginLeft: 6 }}>✓</span>
);
const StarIcon = () => <span style={{ color: "#faad14" }}>★</span>;


const capitalizeWords = (s) =>
  (s || "").toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());
const UploadInterface = (props) => {
  const { id } = useParams();
  const history = useNavigate();

  // UI state
  const [loader, setLoader] = useState(false);
  const [apiData, setApiData] = useState({}); // { name, columns: [] }
  const [mapperFields, setMapperFields] = useState({
    mandatory: [],
    non_mandatory: [],
    all_fields: [],
  });
  const [mapper, setMapper] = useState({}); // { columnName: mappedValue }
  const [checkedFields, setCheckedFields] = useState([]); // selected mapped values
  const [autoMapCheck, setAutoMapCheck] = useState(false);
  const [AutoMap, setAutoMap] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const default_app = localStorage.getItem("default_app") || "Trak Learning";

  // Worksheet selection state
  const [isSheetModalVisible, setIsSheetModalVisible] = useState(false);
  const [availableSheets, setAvailableSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [pendingFileData, setPendingFileData] = useState(null); // { workbook, file }

  // Fetch upload fields from API
  const fetchUploadFields = async () => {
    setLoadingFields(true);
    try {
      const response = await axiosInstance.get(`customer/uploadfields/`);
      if (response?.data?.status === 200 && response?.data?.data) {
        setMapperFields(response.data.data);
      } else {
        message.error("Failed to load upload fields");
      }
    } catch (error) {
      console.error("Error fetching upload fields:", error);
      message.error("Failed to load upload fields");
    } finally {
      setLoadingFields(false);
    }
  };

  // Fetch fields when component mounts
  useEffect(() => {
    fetchUploadFields();
  }, []);

  // Get CSRF token function
  function getCookie(name) {
    const cookies = document.cookie.split("; ");
    for (let i = 0; i < cookies.length; i++) {
      const [cookieName, cookieValue] = cookies[i].split("=");
      if (cookieName === name) {
        return cookieValue;
      }
    }
    return null;
  }

  // derive given array of allowed mapping keys (like original)
  const [givenArray, setGivenArray] = useState([]);
  useEffect(() => {
    const combined = [
      ...(mapperFields?.mandatory || []).map((m) => m.value),
      ...(mapperFields?.non_mandatory || []).map((n) => n.value),
    ];
    setGivenArray(combined);
  }, [mapperFields]);

  /* --------------- Excel file upload handling --------------- */
  const finalizeSheetSelection = async (sheetName, workbook, file, onSuccess) => {
    try {
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      const columns = jsonData[0] || [];

      if (columns.length === 0) {
        message.error("Selected sheet appears to be empty or has no headers");
        setLoader(false);
        return;
      }

      const fileData = {
        id: "local-" + Date.now(),
        name: file?.name || "uploaded.xlsx",
        columns: columns.filter(Boolean),
        rowCount: jsonData.length - 1,
        sheetName: sheetName,
      };

      const csrfToken = getCookie("csrftoken");
      const formData = new FormData();
      formData.append("file", file);

      // We still upload the whole file, but process based on the selected sheet
      const uploadResponse = await axiosInstance.post(
        `/customer/upload/?organization_id=${id}`,
        formData,
        {
          headers: {
            "X-CSRFToken": csrfToken,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const responseData = uploadResponse?.data?.data || uploadResponse?.data;
      if (responseData?.id || responseData?.upload_id) {
        fileData.id = responseData.id || responseData.upload_id;
      }

      setApiData(fileData);
      setLoader(false);
      if (onSuccess) onSuccess("ok");
      message.success(`File processed successfully (Sheet: ${sheetName})`);
    } catch (error) {
      console.error("Error finalizing sheet selection:", error);
      message.error("Failed to process selected sheet.");
      setLoader(false);
    }
  };

  const uploadImage = async ({ file, onSuccess, onError }) => {
    setLoader(true);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        if (workbook.SheetNames.length === 0) {
          message.error("Excel file appears to be empty");
          setLoader(false);
          if (onError) onError("No sheets found");
          return;
        }

        if (workbook.SheetNames.length > 1) {
          // Multiple sheets: show modal
          setAvailableSheets(workbook.SheetNames);
          setSelectedSheet(workbook.SheetNames[0]);
          setPendingFileData({ workbook, file, onSuccess });
          setIsSheetModalVisible(true);
          // Loader is still true, but modal is shown. 
          // We'll turn it off if they cancel or after selection is done.
        } else {
          // Single sheet: proceed directly
          finalizeSheetSelection(workbook.SheetNames[0], workbook, file, onSuccess);
        }
      } catch (error) {
        console.error("Error parsing Excel file:", error);
        message.error("Failed to parse Excel file. Please check the file format.");
        setLoader(false);
        if (onError) onError(error);
      }
    };

    reader.onerror = () => {
      message.error("Failed to read file");
      setLoader(false);
      if (onError) onError("File read error");
    };

    reader.readAsArrayBuffer(file);
  };

  const handleSheetOk = () => {
    if (!selectedSheet) {
      message.warn("Please select a worksheet");
      return;
    }
    const { workbook, file, onSuccess } = pendingFileData;
    setIsSheetModalVisible(false);
    finalizeSheetSelection(selectedSheet, workbook, file, onSuccess);
  };

  const handleSheetCancel = () => {
    setIsSheetModalVisible(false);
    setLoader(false);
    setPendingFileData(null);
  };

  const draggerprops = {
    name: "file",
    multiple: false,
    accept:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel",
    customRequest: uploadImage,
    beforeUpload: (file) => {
      // allow any extension for demo but give a warning if not xlsx
      const ok =
        file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.name?.toLowerCase().endsWith(".xlsx") ||
        file.name?.toLowerCase().endsWith(".xls");
      if (!ok) {
        message.warn(
          `${file.name} may not be an Excel file (allowed: .xlsx/.xls)`
        );
      }
      return true; // let customRequest handle it
    },
  };

  /* Download Excel template from API */
  const downloadTemplate = async () => {
    try {
      setLoader(true);
      const csrfToken = getCookie("csrftoken");

      // First, get the file URL from API
      const response = await axiosInstance.get("/customer/downloadtemplate/", {
        headers: {
          "X-CSRFToken": csrfToken,
        },
      });

      // Extract file_url from response
      const fileUrl = response?.data?.file_url;

      if (!fileUrl) {
        throw new Error("File URL not found in response");
      }

      // Fetch the actual file from the URL
      const fileResponse = await fetch(fileUrl);

      if (!fileResponse.ok) {
        throw new Error("Failed to fetch file from URL");
      }

      // Get the file as blob
      const blob = await fileResponse.blob();

      // Extract filename from URL or use default
      const urlParts = fileUrl.split("/");
      let filename = urlParts[urlParts.length - 1] ||
        (default_app === "Trak Coaching"
          ? "coachee_upload_template.xlsx"
          : "learner_upload_template.xlsx");

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success("Template downloaded successfully");
    } catch (error) {
      console.error("Error downloading template:", error);
      message.error(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to download template. Please try again."
      );
    } finally {
      setLoader(false);
    }
  };

  /* Cancel: go back to upload manager */
  const cancelUploadProcess = () => {
    if (props?.setCurrent) {
      props.setCurrent("upload");
    }
  };

  /* When user picks mapping for a column */
  const setMapperData = (col, val) => {
    const next = { ...mapper };
    if (!val) {
      delete next[col];
    } else {
      next[col] = val;
    }
    setMapper(next);

    // update checkedFields: values currently used in mapper
    const used = Object.values(next).filter(Boolean);
    setCheckedFields(used);
  };

  /* Validation: ensure all mandatory fields are mapped */
  const validate = () => {
    if (!mapperFields?.mandatory || mapperFields.mandatory.length === 0) {
      return true; // If no mandatory fields, allow submission
    }
    const mandatoryValues = mapperFields.mandatory.map((m) => m.value);
    return mandatoryValues.some((f) => !Object.values(mapper).includes(f));
  };

  /* AutoMap detection: checks if any column matches field names (case-insensitive, exact match) */
  useEffect(() => {
    if (apiData?.columns && mapperFields?.all_fields?.length > 0) {
      // Check if any Excel column header exactly matches any field key (case-insensitive)
      const columnsMatch = apiData.columns.some((col) => {
        const normalizedCol = col.toString().toLowerCase().trim();
        return mapperFields.all_fields.some((field) => {
          const normalizedKey = field.key.toLowerCase().trim();
          const normalizedValue = field.value.toLowerCase().trim();
          return normalizedCol === normalizedKey || normalizedCol === normalizedValue;
        });
      });
      setAutoMap(columnsMatch);
    } else {
      setAutoMap(false);
    }
  }, [apiData?.columns, mapperFields]);

  /* When autoMapCheck toggles ON, perform exact matching (case-insensitive) */
  useEffect(() => {
    if (AutoMap && apiData?.columns && autoMapCheck && mapperFields?.all_fields) {
      const next = {};
      apiData.columns.forEach((col) => {
        const normalizedCol = col.toString().toLowerCase().trim();
        // Find exact match (case-insensitive) with field key
        const match = mapperFields.all_fields.find((f) => {
          const normalizedKey = f.key.toLowerCase().trim();
          const normalizedValue = f.value.toLowerCase().trim();
          return normalizedCol === normalizedKey || normalizedCol === normalizedValue;
        });
        if (match) {
          next[col] = match.value;
        }
      });
      setMapper(next);
      setCheckedFields(Object.values(next));
    } else if (!autoMapCheck) {
      // if turned off, clear mapper but keep apiData
      setMapper({});
      setCheckedFields([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoMapCheck, AutoMap, apiData?.columns, mapperFields]);

  /* Submit: send mapped data to backend */
  const handleSubmit = async () => {
    if (validate()) {
      const mandatoryFieldNames = mapperFields?.mandatory
        ?.map((m) => m.key)
        .join(", ") || "mandatory fields";
      message.error(`Please map all mandatory fields (${mandatoryFieldNames}) before submitting.`);
      return;
    }

    // Check if upload_id exists
    if (!apiData?.id) {
      message.error("Upload ID not found. Please upload the file again.");
      return;
    }

    setLoader(true);

    try {
      const csrfToken = getCookie("csrftoken");

      // Prepare the payload with mapped_columns
      // mapper object has Excel column names as keys and field values as values
      const payload = {
        mapped_columns: mapper,
      };

      // Call API with organization_id and upload_id as query parameters
      const response = await axiosInstance.post(
        `/customer/userbulkcreate/?organization_id=${id}&upload_id=${apiData.id}`,
        payload,
        {
          headers: {
            "X-CSRFToken": csrfToken,
            "Content-Type": "application/json",
          },
        }
      );

      message.success("Users created successfully.");

      // Navigate back to upload manager
      // Navigate to upload details
      if (props?.setUploadId) {
        props.setUploadId(apiData.id);
      }
      if (props?.setCurrent) {
        props.setCurrent("upload_details");
      }

      // Optionally reload the upload list if there's a callback
      if (props?.LoadUser) {
        props.LoadUser();
      }
    } catch (error) {
      console.error("Error creating users:", error);
      message.error(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to create users. Please try again."
      );
    } finally {
      setLoader(false);
    }
  };

  if (loadingFields) {
    return (
      <div style={{ textAlign: "center", padding: 50 }}>
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>Loading upload fields...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="tableCourses" style={{ padding: 16 }}>
        {loader && (
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <Spin />
          </div>
        )}

        <div
          className="track-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              {capitalizeWords(
                default_app === "Trak Coaching"
                  ? "NEW COACHEE EXCEL UPLOAD"
                  : "NEW LEARNERS EXCEL UPLOAD"
              )}
            </div>
          </div>
          <div>
            <Button
              type="default"
              icon={<CloudDownloadOutlined />}
              onClick={downloadTemplate}
            >
              Download Sample Excel Sheet
            </Button>
          </div>
        </div>

        {/* Side by Side Layout: Upload on Left, Field Mapping on Right */}
        <div
          style={{
            display: "flex",
            gap: 24,
            marginTop: 24,
          }}
        >
          {/* Left Section - File Upload */}
          <div style={{ flex: "0 0 30%", minWidth: 300 }}>
            {!apiData?.name ? (
              <>
                <div style={{ marginTop: 40, marginBottom: 12, textAlign: "center" }}>
                  <Upload {...draggerprops} showUploadList={false}>
                    <Button icon={<UploadOutlined />} size="small">Upload manually</Button>
                  </Upload>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <Dragger
                    {...draggerprops}
                    showUploadList={false}
                    style={{ minHeight: 120 }}
                  >
                    <p className="ant-upload-drag-icon">
                      <UploadOutlined style={{ fontSize: 36 }} />
                    </p>
                    <p className="ant-upload-text" style={{ fontSize: 13 }}>Drop File to upload</p>
                    <p className="ant-upload-hint" style={{ fontSize: 12 }}>.xlsx .xls</p>
                  </Dragger>
                </div>

                <div style={{ textAlign: "center" }}>
                  <Button onClick={cancelUploadProcess} size="small">Cancel Upload</Button>
                </div>
              </>
            ) : (
              <>
                <div style={{ marginTop: 40 }}>
                  <Card style={{ marginBottom: 12, padding: 12 }}>
                    <p style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{apiData?.name}</p>
                    <p style={{ marginBottom: 8, fontSize: 12, color: "#666" }}>
                      is selected for field mapping
                    </p>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Button
                        size="small"
                        onClick={() => {
                          setApiData({});
                          setMapper({});
                          setCheckedFields([]);
                          setAutoMapCheck(false);
                        }}
                      >
                        Remove File
                      </Button>
                    </div>
                  </Card>
                </div>

                <div style={{ textAlign: "center" }}>
                  <Button onClick={cancelUploadProcess} size="small">Cancel Upload</Button>
                </div>
              </>
            )}
          </div>

          {/* Right Section - Field Mapping */}
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 10 }}>
                Field Mapping
              </div>
            </div>

            <div
              className="data-section"
              style={{ border: "1px solid #eee", borderRadius: 6, padding: 10 }}
            >
              <div style={{ display: "flex", gap: 12 }}>
                <div className="cols src-cols" style={{ flex: 2.5 }}>
                  {/* Column Headers */}
                  <div style={{ display: "flex", gap: 8, marginBottom: 8, fontSize: 13 }}>
                    <div style={{ flex: 1, fontWeight: 500 }}>
                      Source file columns
                    </div>
                    <div style={{ flex: 1, fontWeight: 500, paddingLeft: "6px" }}>Mapped columns</div>
                  </div>

                  {apiData?.columns ? (
                    apiData.columns.map((col, i) => (
                      <div
                        key={`mapper-column-${i}`}
                        style={{
                          display: "flex",
                          gap: 8,
                          padding: "6px 0",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            padding: "4px 6px",
                            background: i % 2 === 0 ? "#fafafa" : "transparent",
                            borderRadius: 4,
                            fontSize: 13,
                          }}
                        >
                          {col}
                        </div>

                        <div style={{ flex: 1, marginLeft: "-8px" }}>
                          <Select
                            value={mapper[col]}
                            placeholder="Select mapping column"
                            style={{ width: "100%", fontSize: 13 }}
                            size="small"
                            onChange={(val) => setMapperData(col, val)}
                            allowClear
                          >
                            {mapperFields?.all_fields?.map((field, fi) => (
                              <Select.Option
                                value={field.value}
                                key={`mapper-selector-${fi}`}
                                disabled={
                                  checkedFields.includes(field.value) &&
                                  mapper[col] !== field.value
                                }
                              >
                                {field.key}
                              </Select.Option>
                            ))}
                          </Select>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: 12 }}>
                      <div
                        style={{
                          padding: 6,
                          background: "#fafafa",
                          borderRadius: 4,
                          fontSize: 13,
                        }}
                      >
                        --
                      </div>
                    </div>
                  )}
                </div>

                <div className="mandatory-field-indication" style={{ flex: 1, fontSize: 12 }}>
                  {AutoMap && (
                    <div style={{ fontSize: 13, marginBottom: 12 }}>
                      Auto MAP{" "}
                      <Checkbox
                        checked={autoMapCheck}
                        onChange={(e) => setAutoMapCheck(e.target.checked)}
                        size="small"
                      />
                    </div>
                  )}

                  <p style={{ fontWeight: 700, marginBottom: 6, fontSize: 13 }}>
                    Mandatory Fields <StarIcon />
                  </p>

                  {mapperFields?.mandatory?.map((man, i) => (
                    <div
                      key={`mandatory-field-${i}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "4px 0",
                      }}
                    >
                      <div style={{ flex: 1, fontSize: 12 }}>{man.key}</div>
                      <div>
                        {Object.values(mapper).includes(man.value) ? (
                          <CheckMark />
                        ) : null}
                      </div>
                    </div>
                  ))}

                  <p style={{ fontWeight: 700, marginTop: 10, marginBottom: 6, fontSize: 13 }}>
                    Optional Fields
                  </p>

                  {mapperFields?.non_mandatory?.map((nman, i) => (
                    <div
                      key={`optional-field-${i}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "4px 0",
                      }}
                    >
                      <div style={{ flex: 1, fontSize: 12 }}>{nman.key}</div>
                      <div>
                        {Object.values(mapper).includes(nman.value) ? (
                          <CheckMark />
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 10, textAlign: "right" }}>
              <Button
                type="primary"
                onClick={handleSubmit}
                disabled={validate() || !apiData?.id}
                size="small"
              >
                Submit
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Modal
        title="Worksheet"
        open={isSheetModalVisible}
        onOk={handleSheetOk}
        onCancel={handleSheetCancel}
        okText="Ok"
        cancelText="Cancel"
        centered
        width={600}
      >
        <div style={{ padding: "20px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <div style={{ fontWeight: 600, color: "#2d4059", minWidth: 140 }}>
              Select a Worksheet
            </div>
            <div style={{ flex: 1 }}>
              <Select
                style={{ width: "100%" }}
                value={selectedSheet}
                onChange={(val) => setSelectedSheet(val)}
              >
                {availableSheets.map((sheet) => (
                  <Select.Option key={sheet} value={sheet}>
                    {sheet}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UploadInterface;
